import KmxArrowDownRightCircleFilled from '@kmx/mui-icons/KmxArrowDownRightCircleFilled';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import { memo, useCallback } from 'react';

import { TaggingEvent } from '@/context/tagging';
import { formatCurrency } from '@/helpers/formatters';
import { buildCarTitle } from '@/helpers/normalizers';
import useTagging from '@/hooks/useTagging';
import InfoButton from '@/shared/InfoButton';

import type { FeaturedCarTileProps } from './FeaturedCarTileProps';

const Heading = styled(Typography)<TypographyProps>(({ theme }) => ({
  ...theme.typography.h5,

  '[data-orientation="vertical"] &': {
    ...theme.typography.body1,
    fontWeight: 700,
  },

  '[data-orientation="vertical"][data-variant="mini"] &': {
    ...theme.typography.body1,
    fontWeight: 700,
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

const Body2Text = styled(Typography)<TypographyProps>(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,

  '[data-orientation="vertical"][data-variant="mini"] &': {
    fontSize: '0.75rem',
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

const MonthlyPayment = styled(Typography)<TypographyProps>(() => ({
  fontWeight: 700,
  textDecoration: 'underline',
}));

type DetailsProps = {
  headingIsLink?: boolean;
  car: FeaturedCarTileProps['car'];
  monthlyPayment?: FeaturedCarTileProps['monthlyPayment'];
};

function Details({ car, monthlyPayment, headingIsLink = false }: DetailsProps) {
  const { dispatchTaggingEvent } = useTagging();
  const href = `/car/${car.stockNumber}`;
  const miles = (Math.ceil(car.mileage / 1000) * 1000).toLocaleString('en-US');

  const hasPriceDrop = car.statusInfo.hasPriceDrop;
  const formattedPrice = formatCurrency(car.price);
  const formattedOriginalPrice =
    hasPriceDrop && car.originalPrice
      ? formatCurrency(car.originalPrice)
      : null;

  const formattedMonthlyPayment =
    monthlyPayment?.amount === undefined
      ? ''
      : formatCurrency(monthlyPayment.amount);

  const handleInfoButtonClick = useCallback(() => {
    dispatchTaggingEvent({
      event: TaggingEvent.ClickInfoIcon,
      data: { text: 'info icon' },
    });
    monthlyPayment?.onInfoButtonClick?.();
  }, [dispatchTaggingEvent, monthlyPayment]);

  const handleLinkClick = useCallback(() => {
    dispatchTaggingEvent({
      event: TaggingEvent.ClickCarTile,
      data: {
        text: buildCarTitle(car),
      },
    });
  }, [car, dispatchTaggingEvent]);

  return (
    <>
      <Heading component="h3">
        {headingIsLink ? (
          <Link
            color="inherit"
            data-ignore-anchor-tag-rule
            href={href}
            onClick={handleLinkClick}
            underline="none"
          >
            {buildCarTitle(car)}
          </Link>
        ) : (
          buildCarTitle(car)
        )}
      </Heading>

      <Body2Text component="div">{miles} miles</Body2Text>

      <Body2Text component="div">{car.location.transferText}</Body2Text>

      <Stack
        alignItems="center"
        columnGap={1}
        direction="row"
        flexWrap="wrap"
        useFlexGap
      >
        {formattedMonthlyPayment && (
          <>
            <Stack
              alignItems="center"
              direction="row"
              spacing={0.5}
              useFlexGap
              sx={{ cursor: 'pointer' }}
              onClick={handleInfoButtonClick}
            >
              <MonthlyPayment
                component="span"
                variant="body1"
                whiteSpace="nowrap"
              >
                Est. {formattedMonthlyPayment}/mo
              </MonthlyPayment>

              {monthlyPayment?.onInfoButtonClick && (
                <InfoButton iconSize={18} variant="stroked" />
              )}
            </Stack>

            <Typography color="textSecondary" component="span" variant="body1">
              &middot;
            </Typography>
          </>
        )}

        <Stack
          spacing={0.25}
          direction="row"
          alignItems="center"
          className={['price-info', hasPriceDrop ? 'has-price-drop' : '']
            .filter(Boolean)
            .join(' ')}
        >
          {hasPriceDrop && (
            <KmxArrowDownRightCircleFilled
              fontSize="small"
              sx={{ color: 'success.light' }}
            />
          )}

          <Typography component="span" fontWeight={700} variant="body1">
            {formattedPrice}*
          </Typography>

          {formattedOriginalPrice && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ textDecoration: 'line-through' }}
            >
              {formattedOriginalPrice}
            </Typography>
          )}
        </Stack>
      </Stack>
    </>
  );
}

export default memo(Details);
